import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";

import { useAgenticLogicRule } from "@src/api/agentic-logic/get-agentic-logic-rule";
import { readRestoredCreationContext } from "@src/features/ai/components/AiChat/agenticLogicCreationContext";
import { cancelActiveAgenticLogicCreation } from "@src/features/ai/components/AiChat/agenticLogicCreationController";
import { useGetChatHistory } from "@src/api/ai/chat-history/use-get-chat-history";
import {
  type AgenticLogicSurface,
  isAgenticLogicPathname,
  resolveAgenticLogicView,
} from "@src/features/ai/pages/agentic-logic/surface/surface";
import { useDispatch, useSelector } from "@src/store";
import {
  clearAiChat,
  getAiChat,
  restoreChatState,
} from "@src/store/slices/aiChat";
import { getAiChatProps, setAiChat } from "@src/store/slices/global";

/**
 * Keeps the Atlas chat rail open on a rule's workspace, and rehydrates the
 * conversation that authored the rule. With no active build the panel is an
 * idle chat; the first message starts a code-entry rule creation session (see
 * `AgenticLogicCreationChat`). The panel is always the inline rail, never a
 * modal (`AiChat` disables the below-`lg` dialog for this mode), so forcing it
 * open never blocks the page.
 *
 * The creation view is deliberately not pinned: it opens the chat itself when
 * a build starts — Ask Atlas or a template card (see `CreateRuleTab`) — so the
 * page shows its entry points, not an empty chat. A session started there
 * stays open (`isAtlasChatLocation` still spans both views); this hook only
 * decides where an idle rail is forced open. The rules list isn't pinned
 * either: it is a browse view whose table runs the full width.
 *
 * A chat already open in another mode is left alone rather than having its
 * transcript destroyed; Atlas takes the panel once that chat is closed.
 *
 * REHYDRATION. The conversation is durable server-side, and the rule row now
 * names the session that authored it, so opening a rule loads that transcript
 * back into the live chat rather than an empty one — the merchant keeps what
 * Atlas said, most importantly the diagnosis of a build that gave up. It is
 * restored into redux (not injected read-only) so the next message continues
 * the same session. Three states degrade rather than fail:
 *   - a rule with no authoring session (created before the association was
 *     recorded, or outside a chat) opens the empty chat exactly as before;
 *   - a transcript that fails to load leaves the empty chat, because the rail
 *     is still usable without it;
 *   - a chat that already carries turns is never overwritten, so an in-flight
 *     build survives navigation between rules.
 * The restore is attempted once per rule per mount, which is what lets "New
 * chat" clear the rail without it immediately refilling.
 *
 * A NEW RULE's conversation is restored the same way, keyed on the session id
 * the creation view carries in its URL (`?rule=new&session=<id>`, written by
 * `AgenticLogicCreationChat` once the session exists). The transcript comes
 * back from the server and the creation context is rebuilt from the entry the
 * session stored (`readRestoredCreationContext`), so a reload lands on the
 * question the conversation had reached — and the next reply continues that
 * session — instead of on a blank start screen. Once per session id per
 * mount, onto an idle chat only: a live session writing its own id into the
 * URL must never fetch itself back over its transcript.
 *
 * Leaving the Atlas chat locations is what closes the panel — see the
 * pathname effect in `LoggedInProvider`.
 */
export const useAtlasChatRail = (surface: AgenticLogicSurface) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { buildingRuleId, open, mode } = useSelector(getAiChatProps);
  const aiChat = useSelector(getAiChat);
  const view = resolveAgenticLogicView(
    search,
    surface.ruleParam,
    surface.sessionParam
  );
  // An embedded surface has no route of its own — its host is a dialog, and
  // this hook only runs while that dialog has mounted the provider — so the
  // view alone decides. Standalone surfaces keep the pathname check.
  const isOnSurface =
    surface.kind === "embedded" || isAgenticLogicPathname(pathname);
  // The creation view is pinned only when embedded. The standalone create page
  // carries its own Ask Atlas composer and opens the rail itself when a build
  // starts, so pinning would show an empty chat beside a form that already
  // offers one; the embedded view drops that composer precisely because the
  // rail is the composer there, so it has to be on screen from the start.
  const isPinned =
    isOnSurface &&
    (view.kind === "rule" ||
      (surface.kind === "embedded" && view.kind === "new"));
  const ruleId = view.kind === "rule" ? view.ruleId : null;
  const newRuleSessionId =
    isOnSurface && view.kind === "new" ? view.sessionId : null;

  useEffect(() => {
    if (!isPinned) {
      return;
    }
    // An open chat already owns the panel — whether it is Atlas's own session
    // or another mode's. Only a closed (or mode-less) panel is taken over.
    if (open && mode !== undefined) {
      return;
    }

    dispatch(clearAiChat());
    dispatch(
      setAiChat([
        { key: "open", value: true },
        { key: "mode", value: "agentic-logic-creation" },
        { key: "initialContext", value: null },
        { key: "start", value: false },
        { key: "buildingRuleId", value: null },
        { key: "createdRuleId", value: null },
        { key: "activeScenario", value: null },
      ])
    );
  }, [dispatch, isPinned, mode, open]);

  // The session whose transcript is being fetched, and the rule it was asked
  // for. The second half is the once-per-rule guard: without it, clearing the
  // rail ("New chat") would satisfy the empty-chat condition again and refill
  // it with the transcript the merchant just dismissed.
  //
  // `purpose` says what the transcript is restored as: a rule's authoring
  // history into the idle rail, or a new rule's in-progress creation session,
  // which also needs its creation context back.
  const [sessionToLoad, setSessionToLoad] = useState<{
    sessionId: string;
    purpose: "rule" | "new-rule";
  } | null>(null);
  const attemptedForRuleId = useRef<string | null>(null);
  const attemptedForNewRuleSession = useRef<string | null>(null);

  // The rule whose conversation the rail is showing. A conversation belongs to
  // ONE rule: it is that rule's authoring transcript, its questions are about
  // that rule, and the carts offered beside the composer run against it. So
  // moving to another rule starts that rule's conversation instead of carrying
  // the last one across, where every part of it would be about the wrong rule.
  //
  // Only a move BETWEEN rules clears. The first rule the rail sees is adopted
  // silently, which is what keeps the create flow's `?rule=new` → `?rule=<id>
  // hand-off — the moment the build's own transcript lands on the workspace —
  // from wiping itself.
  //
  // The new-rule view starts a new authoring lifecycle, so it also resets the
  // remembered rule. The rules list does not: rule-1 → list → rule-2 is still
  // a move between existing rules and must clear rule-1's conversation.
  const shownRuleId = useRef<string | null>(null);
  useEffect(() => {
    if (view.kind === "new") {
      shownRuleId.current = null;
      return;
    }
    if (!isPinned || !ruleId) {
      return;
    }
    const previous = shownRuleId.current;
    shownRuleId.current = ruleId;
    if (previous === null || previous === ruleId || buildingRuleId === ruleId) {
      return;
    }

    // The stream first: a run still writing into the panel would repopulate
    // the rail we are about to clear, mixing the old rule's turns into the new
    // rule's conversation.
    cancelActiveAgenticLogicCreation("rule-change");
    dispatch(clearAiChat());
    dispatch(
      setAiChat([
        { key: "initialContext", value: null },
        { key: "start", value: false },
        { key: "buildingRuleId", value: null },
        { key: "createdRuleId", value: null },
        { key: "activeScenario", value: null },
      ])
    );
    // Let the new rule's own transcript load, and drop any request for the old
    // one still in flight — it would otherwise land on the empty panel this
    // just produced.
    attemptedForRuleId.current = null;
    setSessionToLoad(null);
  }, [buildingRuleId, dispatch, isPinned, ruleId, view.kind]);

  const ruleQuery = useAgenticLogicRule(
    isPinned && ruleId ? ruleId : undefined
  );
  const authoringSessionId = ruleQuery.data?.rule.authoringSessionId ?? null;

  const isIdleChat = aiChat.turns.length === 0 && !aiChat.sessionId;

  useEffect(() => {
    if (!isPinned || !ruleId || !authoringSessionId) {
      return;
    }
    if (attemptedForRuleId.current === ruleId) {
      return;
    }
    if (!isIdleChat) {
      return;
    }

    attemptedForRuleId.current = ruleId;
    setSessionToLoad({ sessionId: authoringSessionId, purpose: "rule" });
  }, [authoringSessionId, isIdleChat, isPinned, ruleId]);

  useEffect(() => {
    if (!newRuleSessionId) {
      return;
    }
    if (attemptedForNewRuleSession.current === newRuleSessionId) {
      return;
    }
    // Marked before the idle check: the session that just wrote its own id
    // into the URL is live, and must not be fetched back later either.
    attemptedForNewRuleSession.current = newRuleSessionId;
    if (!isIdleChat) {
      return;
    }

    setSessionToLoad({ sessionId: newRuleSessionId, purpose: "new-rule" });
  }, [isIdleChat, newRuleSessionId]);

  const sessionHistory = useGetChatHistory({
    sessionId: sessionToLoad?.sessionId ?? null,
    enabled: sessionToLoad !== null,
  });

  useEffect(() => {
    if (sessionToLoad === null || !sessionHistory.isSuccess) {
      return;
    }
    const chat = sessionHistory.data ?? null;
    setSessionToLoad(null);
    // Restored only onto a chat that is still empty: the fetch is async, and a
    // merchant who started typing meanwhile owns the panel.
    if (!chat || !isIdleChat) {
      return;
    }

    if (sessionToLoad.purpose === "rule") {
      dispatch(restoreChatState(chat));
      return;
    }

    // A new rule's session: only an Atlas creation conversation with the
    // entry it started from can be resumed, and only while no other mode's
    // chat owns the panel. Anything else leaves the start screen as it is.
    if (open && mode !== undefined && mode !== "agentic-logic-creation") {
      return;
    }
    const context =
      chat.workflowId === WORKFLOW_IDS.AGENTIC_LOGIC_RULE_CREATION
        ? readRestoredCreationContext(chat)
        : null;
    if (!context) {
      return;
    }
    dispatch(restoreChatState(chat));
    dispatch(
      setAiChat([
        { key: "open", value: true },
        { key: "mode", value: "agentic-logic-creation" },
        // A stored entry is what marks the session as running: the creation
        // view leaves its start screen and replies continue this session.
        { key: "initialContext", value: context },
        { key: "start", value: false },
        { key: "buildingRuleId", value: null },
        { key: "createdRuleId", value: null },
        { key: "activeScenario", value: null },
      ])
    );
  }, [
    dispatch,
    isIdleChat,
    mode,
    open,
    sessionHistory.data,
    sessionHistory.isSuccess,
    sessionToLoad,
  ]);
};
